import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { useElectionContext } from '@/shared/election/context'
import { listCandidacies, type Candidacy } from '@/features/candidacy/api'
import {
  getSubject, listSubjects, resolveSubject, setPublicFigure, type Subject,
} from '@/features/dossier/api'
import { Pill, label } from '@/features/dossier/components/primitives'
import { listTargets, openTarget, type Target } from './api'

const SIDES = ['OPPOSITION', 'SPOILER', 'INDEPENDENT', 'ALLY', 'OURS']
const TARGET_TYPES = ['CANDIDATE', 'PARTY', 'COALITION', 'ORGANISATION']
const CLEARANCES = ['OPEN', 'RESTRICTED', 'CLOSE_HOLD', 'COMPARTMENTED']

/**
 * Open a target — the act that puts somebody in Opposition Command.
 *
 * ## Marking a rival is three facts, not one flag
 *
 * There is no "opposition" checkbox on a candidate, and there should not be. A posture is
 * (1) a dossier FILE on the subject, (2) a recorded ruling that they are a PUBLIC FIGURE,
 * with a basis, and (3) the target row itself, naming the election and the side. This form
 * performs all three in order and says so, rather than hiding two of them behind the third.
 *
 * ## Why the candidacy, and not the candidate
 *
 * A candidacy is that person contesting ONE seat, at ONE unit, in ONE election, on ONE
 * party's ticket. Binding to the bare candidate would make the 2027 threat matrix re-label
 * itself when somebody defects in 2032 — the historical record would silently become a claim
 * about the present. `opposition_target.candidacy_id` exists for that reason and this form
 * fills it whenever the subject came from the register.
 *
 * ## The public-figure ruling is shown before it is made
 *
 * Someone already on a ballot is a public figure on the {@code DECLARED_CANDIDACY} basis —
 * that is what being on a ballot means. But the ruling is still an act recorded against the
 * operator's name, so the form states it in a sentence above the button rather than
 * performing it silently. For a subject picked from the dossier the form will NOT rule for
 * you: it links to the file, because the basis there is a judgement somebody has to make.
 */
export function OppositionNewTargetPage() {
  const navigate = useNavigate()
  const { election, electionId } = useElectionContext()

  const [mode, setMode] = useState<'candidacy' | 'subject'>('candidacy')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState<string | null>(null)

  // ── the register side ──────────────────────────────────────────────────────
  const [search, setSearch] = useState('')
  const [candidacies, setCandidacies] = useState<Candidacy[]>([])
  const [picked, setPicked] = useState<Candidacy | null>(null)

  // ── the dossier side ───────────────────────────────────────────────────────
  const [subjectQuery, setSubjectQuery] = useState('')
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [pickedSubject, setPickedSubject] = useState<Subject | null>(null)

  /**
   * Subject ids already under command in THIS election. A target is unique on
   * (election, subject), so offering one twice would spend three calls to earn a 409 that
   * says what this list already knows.
   */
  const [taken, setTaken] = useState<Set<string>>(new Set())

  // ── the posture ────────────────────────────────────────────────────────────
  const [sideCode, setSideCode] = useState('OPPOSITION')
  const [targetTypeCode, setTargetTypeCode] = useState('CANDIDATE')
  const [clearanceCode, setClearanceCode] = useState('OPEN')
  const [priority, setPriority] = useState(5)

  const loadCandidacies = useCallback(async () => {
    if (!electionId) return
    try {
      setError(null)
      setCandidacies(await listCandidacies(electionId, {
        search: search.trim() || undefined, size: 25,
      }))
    } catch (err) {
      setError(explain(err))
    }
  }, [electionId, search])

  const loadSubjects = useCallback(async () => {
    try {
      setError(null)
      setSubjects(await listSubjects({ q: subjectQuery.trim() || undefined, size: '25' }))
    } catch (err) {
      setError(explain(err))
    }
  }, [subjectQuery])

  useEffect(() => {
    if (!electionId) return
    void listTargets(electionId)
      .then((rows: Target[]) => setTaken(new Set(rows.map((t) => t.subjectId))))
      // Not fatal: without it the server still refuses the duplicate, with the same reason.
      .catch(() => setTaken(new Set()))
  }, [electionId])

  useEffect(() => { if (mode === 'candidacy') void loadCandidacies() }, [mode, loadCandidacies])
  useEffect(() => { if (mode === 'subject') void loadSubjects() }, [mode, loadSubjects])

  // Null is a real state, and the page has to say a sentence rather than show an empty grid:
  // a target names an election, so without one there is nothing this form could submit.
  if (!electionId) {
    return (
      <div className="space-y-3">
        <SectionHeader title="Open a target"
                       description="A posture belongs to one election." />
        <Panel>
          <p className="p-6 text-center text-[12px] text-muted-foreground">
            Choose an election in the bar above. A target is opened against a candidate in a
            named race — there is no election-independent version of the question.
          </p>
        </Panel>
      </div>
    )
  }

  const ready = mode === 'candidacy' ? picked != null
    : pickedSubject != null && pickedSubject.publicFigure && !taken.has(pickedSubject.id)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!ready || !electionId) return
    setBusy(true)
    setError(null)
    try {
      let subjectId: string
      let candidacyId: string | null = null
      let seatType: string | null = null
      let unitId: string | null = null

      if (mode === 'candidacy' && picked) {
        setStep('Opening the dossier file')
        // A CANDIDATE subject is identified by the register's own row id, so this is
        // find-or-create: a second target on somebody already in the dossier reuses the file
        // rather than opening a duplicate one.
        const resolved = await resolveSubject({
          subjectTypeCode: 'CANDIDATE',
          subjectRef: picked.candidateId,
          displayName: picked.candidateName ?? picked.ballotName,
          legalBasisCode: 'PUBLIC_RECORD',
        })
        subjectId = resolved.subjectId
        candidacyId = picked.id
        seatType = picked.seatTypeCode
        unitId = picked.unitId

        const detail = await getSubject(subjectId)
        if (!detail.subject.publicFigure) {
          setStep('Recording the public-figure ruling')
          await setPublicFigure(subjectId, true, 'DECLARED_CANDIDACY',
            `On the ballot in ${election?.name ?? 'this election'} for `
            + `${label(picked.seatTypeCode)}${picked.unitName ? ` at ${picked.unitName}` : ''}.`)
        }
      } else if (pickedSubject) {
        subjectId = pickedSubject.id
      } else {
        return
      }

      setStep('Opening the target')
      const target = await openTarget({
        electionId,
        subjectId,
        candidacyId,
        targetTypeCode: mode === 'candidacy' ? 'CANDIDATE' : targetTypeCode,
        sideCode,
        seatType,
        unitId,
        clearanceCode,
        priority,
      })
      navigate(`/opposition/targets/${target.id}`)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
      setStep(null)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <SectionHeader
        title="Open a target"
        description="A file on the subject, a recorded public-figure ruling, and the posture
                     itself — in that order."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="space-y-2 p-3">
          <div className="flex gap-1">
            {(['candidacy', 'subject'] as const).map((m) => (
              <button key={m} type="button"
                      onClick={() => { setMode(m); setPicked(null); setPickedSubject(null) }}
                      className={`rounded border px-2 py-1 text-[12px] ${
                        mode === m ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground'}`}>
                {m === 'candidacy' ? 'From the candidacy register' : 'From a dossier file'}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {mode === 'candidacy'
              ? 'Anybody on the ballot in this election. The target binds to the CANDIDACY, so '
                + 'the matrix still reads correctly after a defection.'
              : 'For a party, a coalition, an organisation, or a person who contests no seat. '
                + 'They must already be recorded as a public figure.'}
          </p>
        </div>
      </Panel>

      {mode === 'candidacy' ? (
        <Panel>
          <div className="flex flex-wrap items-end gap-2 border-b border-border p-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Search the register
              </span>
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Name on the ballot…"
                     className="h-7 w-64 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>
            <span className="pb-1 text-[11px] text-muted-foreground">
              {election?.name ?? 'Selected election'}
            </span>
          </div>
          <ErpTable head={<>
            <Th>Candidate</Th>
            <Th>Seat</Th>
            <Th>Where</Th>
            <Th>Party</Th>
            <Th>Ballot</Th>
          </>}>
            {candidacies.length === 0 && (
              <EmptyRow colSpan={5}
                        message="No candidacies match. The register is filled in Candidacy &
                                 Nominations." />
            )}
            {candidacies.map((c) => (
              <tr key={c.id}
                  onClick={() => setPicked(c)}
                  className={`cursor-pointer ${picked?.id === c.id
                    ? 'bg-primary/10' : 'hover:bg-muted/40'}`}>
                <Td>
                  <span className="font-medium">{c.candidateName ?? c.ballotName}</span>
                  {c.candidateName && c.ballotName !== c.candidateName && (
                    <span className="ml-1.5 text-[11px] text-muted-foreground">
                      on the paper: {c.ballotName}
                    </span>
                  )}
                </Td>
                <Td>{label(c.seatTypeCode)}</Td>
                <Td>{c.unitName ?? '—'}</Td>
                <Td>
                  {c.independent ? <Pill className="border-border">Independent</Pill>
                    : c.partyShortName ?? c.partyName ?? '—'}
                </Td>
                <Td>
                  {c.onBallot
                    ? <Pill className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700
                                       dark:text-emerald-400">On ballot</Pill>
                    // Not yet cleared is not a bar to opening a target — an aspirant who may
                    // never be certified is exactly who recon is for. It is shown so the
                    // operator knows which they picked.
                    : <Pill className="border-border">{label(c.nominationStatusCode)}</Pill>}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      ) : (
        <Panel>
          <div className="flex flex-wrap items-end gap-2 border-b border-border p-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Search the dossier
              </span>
              <input value={subjectQuery} onChange={(e) => setSubjectQuery(e.target.value)}
                     placeholder="Name contains…"
                     className="h-7 w-64 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>
          </div>
          <ErpTable head={<>
            <Th>Subject</Th>
            <Th>Type</Th>
            <Th>Public figure</Th>
            <Th>Items</Th>
          </>}>
            {subjects.length === 0 && (
              <EmptyRow colSpan={4} message="No files match." />
            )}
            {subjects.map((s) => {
              const selectable = s.publicFigure && !taken.has(s.id)
              return (
              <tr key={s.id}
                  onClick={() => selectable && setPickedSubject(s)}
                  className={`${selectable ? 'cursor-pointer hover:bg-muted/40' : 'opacity-60'} ${
                    pickedSubject?.id === s.id ? 'bg-primary/10' : ''}`}>
                <Td><span className="font-medium">{s.displayName}</span></Td>
                <Td>{label(s.subjectTypeCode)}</Td>
                <Td>
                  {taken.has(s.id)
                    ? <span className="text-[11px] text-muted-foreground">
                        Already under command in this election
                      </span>
                    : s.publicFigure
                    ? <Pill className="border-sky-500/30 bg-sky-500/10 text-sky-700
                                       dark:text-sky-400">
                        {label(s.publicFigureBasisCode)}
                      </Pill>
                    // A 422 at submit would tell them the same thing three fields later.
                    : <span className="text-[11px] text-muted-foreground">
                        Not recorded —{' '}
                        <a href={`/dossier/${s.id}`} className="underline"
                           onClick={(e) => e.stopPropagation()}>
                          rule on the file
                        </a>
                      </span>}
                </Td>
                <Td className="tabular-nums">{s.itemCount}</Td>
              </tr>
              )
            })}
          </ErpTable>
        </Panel>
      )}

      <Panel>
        <div className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Side
            </span>
            <select value={sideCode} onChange={(e) => setSideCode(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              {SIDES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
            <span className="text-[10px] text-muted-foreground">
              Ally and Ours are real postures — the matrix covers the whole field, not only
              the rivals.
            </span>
          </label>

          {mode === 'subject' && (
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Target type
              </span>
              <select value={targetTypeCode}
                      onChange={(e) => setTargetTypeCode(e.target.value)}
                      className="h-7 rounded border border-input bg-background px-2
                                 text-[12px]">
                {TARGET_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
              </select>
            </label>
          )}

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Clearance
            </span>
            <select value={clearanceCode} onChange={(e) => setClearanceCode(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              {CLEARANCES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
            </select>
            <span className="text-[10px] text-muted-foreground">
              Close-hold rows are omitted by the server for anyone without the grant — the
              existence of the target is itself the sensitive fact.
            </span>
          </label>

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Priority (1 highest)
            </span>
            <input type="number" min={1} max={10} value={priority}
                   onChange={(e) => setPriority(Number(e.target.value))}
                   className="h-7 rounded border border-input bg-background px-2 text-[12px]" />
          </label>
        </div>
      </Panel>

      <Panel>
        <div className="space-y-1 p-3">
          <p className="text-[12px] font-medium">What this will do</p>
          {mode === 'candidacy' ? (
            <ol className="list-decimal space-y-0.5 pl-5 text-[11px] text-muted-foreground">
              <li>
                Open or reuse the dossier file on{' '}
                <b>{picked ? picked.candidateName ?? picked.ballotName : 'the candidate'}</b>.
              </li>
              <li>
                Record the public-figure ruling on the basis{' '}
                <b>{label('DECLARED_CANDIDACY')}</b>, against your name, if it is not already
                recorded.
              </li>
              <li>
                Open a <b>{label(sideCode)}</b> target in{' '}
                <b>{election?.name ?? 'the selected election'}</b>.
              </li>
            </ol>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Open a <b>{label(sideCode)}</b> target on{' '}
              <b>{pickedSubject?.displayName ?? 'the selected subject'}</b> in{' '}
              <b>{election?.name ?? 'the selected election'}</b>. The ruling on the file is
              used as it stands — this form will not make one for you.
            </p>
          )}
        </div>
      </Panel>

      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || !ready}
                className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                           text-primary-foreground disabled:opacity-50">
          {busy ? `${step ?? 'Working'}…` : 'Open the target'}
        </button>
        <button type="button" onClick={() => navigate('/opposition')}
                className="rounded border border-border px-3 py-1.5 text-[12px]">
          Cancel
        </button>
        {!ready && (
          <span className="text-[11px] text-muted-foreground">
            {mode === 'candidacy'
              ? 'Pick a candidacy above.'
              : 'Pick a subject who is recorded as a public figure.'}
          </span>
        )}
      </div>
    </form>
  )
}
